import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const ProductionDetailsModal = ({ productionId, onClose }) => {
  const [production, setProduction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await chemicalService.getProductionById(productionId);
        setProduction(res.data);
      } catch (err) {
        console.error('Error loading production:', err);
        setProduction(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [productionId]);

  if (loading) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: '60px', textAlign: 'center', color: '#7f8c8d' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
            Loading production details...
          </div>
        </div>
      </div>
    );
  }

  if (!production) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: '60px', textAlign: 'center', color: '#e74c3c' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
            Failed to load details
            <div style={{ marginTop: '20px' }}>
              <button onClick={onClose} style={styles.closeFooterButton}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalCost = parseFloat(production.total_cost) || 0;
  const actualOutput = parseFloat(production.actual_output) || 0;
  const costPerUnit = actualOutput > 0 ? totalCost / actualOutput : 0;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>⚗ Production Details</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {production.batch_no} · {production.product_name}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={{ padding: '24px 30px' }}>

          {/* ─── Summary stats ─── */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Formula</div>
              <div style={styles.statValueSmall}>{production.formula_code}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Multiplier</div>
              <div style={styles.statValue}>{parseFloat(production.multiplier).toFixed(1)}x</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Expected Output</div>
              <div style={styles.statValue}>
                {parseFloat(production.expected_output).toFixed(2).replace(/\.?0+$/, '')} <span style={styles.unit}>{production.product_unit}</span>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Actual Output</div>
              <div style={{ ...styles.statValue, color: '#27ae60' }}>
                {actualOutput.toFixed(2).replace(/\.?0+$/, '')} <span style={styles.unit}>{production.product_unit}</span>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Total Cost</div>
              <div style={{ ...styles.statValue, color: '#e74c3c' }}>
                Rs {Math.round(totalCost).toLocaleString('en-PK')}
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Cost per {production.product_unit}</div>
              <div style={styles.statValue}>
                Rs {costPerUnit.toFixed(2)}
              </div>
            </div>
          </div>

          {/* ─── Consumed materials ─── */}
          <div style={styles.sectionLabel}>Raw Materials Consumed</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>Material</th>
                  <th style={styles.th}>From Batch</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Quantity</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {(production.consumed || []).map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600' }}>{c.product_name}</div>
                      <div style={{ fontSize: '12px', color: '#95a5a6' }}>{c.product_code}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.batchTag}>{c.source_batch_no}</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700' }}>
                      {parseFloat(c.quantity).toFixed(2).replace(/\.?0+$/, '')} {c.product_unit}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      Rs {Math.round(parseFloat(c.cost || 0)).toLocaleString('en-PK')}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#f8f9fa', fontWeight: '700' }}>
                  <td style={styles.td} colSpan="3">Total</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    Rs {Math.round(totalCost).toLocaleString('en-PK')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ─── Meta ─── */}
          <div style={styles.metaGrid}>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Production Date</span>
              <span style={styles.metaValue}>{formatFullDate(production.production_date)}</span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Completed At</span>
              <span style={styles.metaValue}>{formatFullDateTime(production.completed_at)}</span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Status</span>
              <span style={styles.statusBadge}>✓ {production.status || 'Completed'}</span>
            </div>
          </div>

          {production.notes && (
            <div style={styles.notesBox}>
              <strong>Notes:</strong> {production.notes}
            </div>
          )}

        </div>

        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.closeFooterButton}>Close</button>
        </div>

      </div>
    </div>
  );
};

const formatFullDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

const formatFullDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '900px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '12px', marginBottom: '24px',
  },
  statCard: {
    background: '#f8f9fa', padding: '14px 16px',
    borderRadius: '10px',
  },
  statLabel: {
    fontSize: '11px', color: '#7f8c8d', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.3px',
    marginBottom: '6px',
  },
  statValue: { fontSize: '20px', fontWeight: '700', color: '#2c3e50' },
  statValueSmall: { fontSize: '16px', fontWeight: '700', color: '#e74c3c' },
  unit: { fontSize: '13px', color: '#7f8c8d', fontWeight: '500' },

  sectionLabel: {
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0',
  },
  tableWrap: {
    border: '1px solid #e0e0e0', borderRadius: '10px',
    overflow: 'hidden', marginBottom: '24px',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '12px 14px', textAlign: 'left',
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  td: { padding: '12px 14px', fontSize: '14px', color: '#2c3e50' },
  batchTag: {
    background: '#e3f2fd', color: '#1565c0',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '600',
    fontFamily: 'monospace',
  },

  metaGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px', padding: '14px 18px',
    background: '#f8f9fa', borderRadius: '10px',
  },
  metaItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  metaLabel: {
    fontSize: '11px', color: '#7f8c8d', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  metaValue: { fontSize: '14px', color: '#2c3e50', fontWeight: '600' },
  statusBadge: {
    background: '#d4edda', color: '#155724',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '700',
    display: 'inline-block', width: 'fit-content',
  },
  notesBox: {
    marginTop: '16px',
    background: '#fffbea', padding: '12px 14px',
    borderRadius: '8px', fontSize: '13px',
    color: '#856404', borderLeft: '4px solid #f39c12',
  },

  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end',
    position: 'sticky', bottom: 0, background: 'white',
  },
  closeFooterButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default ProductionDetailsModal;